import React from 'react';
import { toast as sonnerToast, ExternalToast, Toaster } from 'sonner';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import WarningAmber from '@mui/icons-material/WarningAmber';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { overlayZIndex } from 'pages/components/Modal/Modal.styled';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'default';

export type ToastOptions = {
  type?: ToastType;
  icon?: React.ReactNode;
  duration?: number;
  id?: string | number;
  description?: React.ReactNode;
};

const iconSx = { fontSize: 18 } as const;

/** Default on-screen time (Sonner default is 4000). */
const DEFAULT_TOAST_MS = 8000;

const defaultIcons: Record<Exclude<ToastType, 'default'>, React.ReactNode> = {
  success: <CheckCircle style={{ ...iconSx, color: '#4CAF50' }} />,
  error: <ErrorOutline style={{ ...iconSx, color: '#eb364b' }} />,
  warning: <WarningAmber style={{ ...iconSx, color: '#FF9800' }} />,
  info: <InfoOutlined style={{ ...iconSx, color: '#2196F3' }} />,
};

const toMessage = (message: unknown): string | React.ReactNode => {
  if (typeof message === 'string' || React.isValidElement(message)) {
    return message as string | React.ReactElement;
  }
  if (message instanceof Error) return message.message || 'Something went wrong';
  if (message == null) return '';
  if (typeof message === 'object' && message !== null && 'message' in message) {
    const nested = (message as { message?: unknown }).message;
    if (typeof nested === 'string' && nested) return nested;
  }
  // Avoid "[object Object]" from API payloads / Error JSON (often `{}`)
  if (typeof message === 'object') {
    try {
      const asJson = JSON.stringify(message);
      if (asJson && asJson !== '{}' && asJson !== '[]') return asJson;
    } catch {
      /* ignore */
    }
    return 'Something went wrong';
  }
  return String(message);
};

const show = (message: unknown, options?: ToastOptions) => {
  const type = options?.type || 'default';
  const msg = toMessage(message);
  const opts: ExternalToast = {
    icon: options?.icon ?? (type !== 'default' ? defaultIcons[type] : undefined),
    duration: options?.duration ?? DEFAULT_TOAST_MS,
    id: options?.id,
    description: options?.description,
  };

  if (type === 'success') return sonnerToast.success(msg, opts);
  if (type === 'error') return sonnerToast.error(msg, opts);
  if (type === 'warning') return sonnerToast.warning(msg, opts);
  if (type === 'info') return sonnerToast.info(msg, opts);
  return sonnerToast(msg, opts);
};

type ToastFn = {
  (message: unknown, options?: ToastOptions): string | number;
  success: (message: unknown, options?: Omit<ToastOptions, 'type'>) => string | number;
  error: (message: unknown, options?: Omit<ToastOptions, 'type'>) => string | number;
  warning: (message: unknown, options?: Omit<ToastOptions, 'type'>) => string | number;
  info: (message: unknown, options?: Omit<ToastOptions, 'type'>) => string | number;
  dismiss: typeof sonnerToast.dismiss;
};

/** App-wide toast API (Sonner). Compatible with former toastify `toast(msg, { type })`. */
export const toast: ToastFn = Object.assign(show, {
  success: (message: unknown, options?: Omit<ToastOptions, 'type'>) => (
    show(message, { ...options, type: 'success' })
  ),
  error: (message: unknown, options?: Omit<ToastOptions, 'type'>) => (
    show(message, { ...options, type: 'error' })
  ),
  warning: (message: unknown, options?: Omit<ToastOptions, 'type'>) => (
    show(message, { ...options, type: 'warning' })
  ),
  info: (message: unknown, options?: Omit<ToastOptions, 'type'>) => (
    show(message, { ...options, type: 'info' })
  ),
  dismiss: sonnerToast.dismiss,
});

/** Root toaster — mount once in `index.tsx`. */
export const AppToaster = () => (
  <Toaster
    position="top-right"
    expand
    richColors
    closeButton
    duration={DEFAULT_TOAST_MS}
    style={{ zIndex: overlayZIndex + 201 }}
    toastOptions={{
      duration: DEFAULT_TOAST_MS,
      style: { zIndex: overlayZIndex + 201 },
    }}
    icons={{
      success: defaultIcons.success,
      error: defaultIcons.error,
      warning: defaultIcons.warning,
      info: defaultIcons.info,
    }}
  />
);
